import ProductResultTable, {type Product} from "./ProductResultTable";
import React, {useEffect, useState, useMemo, useRef, useCallback} from "react";
import {useApi} from "../hooks/useApi";
import {toast} from "react-toastify";
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar } from './MyAccountLoadingStates';
import FitmentSelector, {type FitmentRuntimeConfig} from './FitmentSelector';

interface SearchableSelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

interface SearchableSelectProps {
    options: SearchableSelectOption[];
    value: string | number;
    placeholder?: string;
    searchPlaceholder?: string;
    onChange: (value: string | number) => void;
    onClear?: () => void;
    disabled?: boolean;
    id?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    options,
    value,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    onChange,
    onClear,
    disabled = false,
    id,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find((o) => String(o.value) === String(value));
    const displayLabel = selectedOption?.label || '';

    const normalisedSearch = search.trim().toLowerCase();
    const filteredOptions = normalisedSearch === ''
        ? options
        : options.filter((o) => String(o.label).toLowerCase().includes(normalisedSearch));

    /* close on outside click */
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    /* auto-focus search input when opening */
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    /* keyboard navigation */
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearch('');
        }
    }, []);

    const handleToggle = () => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
        setSearch('');
    };

    const handleSelect = (optionValue: string | number) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearch('');
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onClear) {
            onClear();
        } else {
            onChange('');
        }
    };

    return (
        <div
            ref={containerRef}
            className={`searchable-select ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
            id={id}
            onKeyDown={handleKeyDown}
        >
            <button
                type="button"
                className="searchable-select__trigger"
                onClick={handleToggle}
                disabled={disabled}
            >
                <span className={`searchable-select__value ${!selectedOption ? 'is-placeholder' : ''}`}>
                    {selectedOption ? displayLabel : placeholder}
                </span>
                {selectedOption && (
                    <span
                        className="searchable-select__clear"
                        onClick={handleClear}
                        role="button"
                        aria-label="Clear selection"
                    >
                        x
                    </span>
                )}
                <span className="searchable-select__arrow">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </button>

            {isOpen && (
                <div className="searchable-select__dropdown">
                    <div className="searchable-select__search">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="searchable-select__options">
                        <button
                            type="button"
                            className={`searchable-select__option ${!selectedOption ? 'is-active' : ''}`}
                            onClick={() => handleSelect('')}
                        >
                            {placeholder}
                        </button>
                        {filteredOptions.map((option) => {
                            const isActive = String(option.value) === String(value);
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`searchable-select__option ${isActive ? 'is-active' : ''} ${option.disabled ? 'is-disabled' : ''}`}
                                    onClick={() => handleSelect(option.value)}
                                    disabled={option.disabled === true}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                        {filteredOptions.length === 0 && (
                            <div className="searchable-select__empty">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

interface Category {
    term_id?: number;
    cat_ID?: number;
    name?: string;
    parent?: number;
    category_parent?: number;
    count?: number;
}

interface CategoryTreeNode {
    id: number | string;
    name: string;
    parent: number | string;
    count?: number;
    children?: CategoryTreeNode[];
}

interface ProductAttributeTerm {
    id: number;
    name: string;
    slug?: string;
    count?: number;
}

interface ProductAttributeFilter {
    taxonomy: string;
    label: string;
    display_type?: 'pill' | 'checkbox' | 'dropdown';
    terms: ProductAttributeTerm[];
}

interface SearchFiltersResponse {
    categories: CategoryTreeNode[];
    attributes: ProductAttributeFilter[];
}

export interface ProductSearchUiSettings {
    header_title?: string;
    header_subtitle?: string;
    hide_header_subtitle?: boolean;
    search_placeholder?: string;
    persistent_category_sidebar?: boolean;
    desktop_collapsible_category_sidebar?: boolean;
    category_tree_collapsed_by_default?: boolean;
    auto_apply_category_filters?: boolean;
    default_category_id?: number;
    category_filter_display?: 'tree' | 'dropdown';
    debounce_keyword_search?: boolean;
    show_upload_csv?: boolean;
    show_add_all_button?: boolean;
    show_attribute_filters?: boolean;
    show_product_image?: boolean;
    show_product_name?: boolean;
    show_product_description?: boolean;
    rrp_source?: string;
    rrp_meta_key?: string;
    show_rrp_strikethrough?: boolean;
    allow_multiple_categories?: boolean;
    use_woocommerce_product_links?: boolean;
    show_backorder_label_on_add_to_cart?: boolean;
    visible_columns?: string[];
}

export type ProductSearchFormProps = {
    categories?: {
        main: Category[],
        sub: Category[],
        sub2: Category[]
    },
    categoryOptions?: CategoryTreeNode[];
    attributes?: ProductAttributeFilter[];
    client_endpoint?: string;
    templateCsvUrl?: string;
    uiSettings?: ProductSearchUiSettings;
    uiSettingsOverride?: ProductSearchUiSettings;
    fitmentTool?: FitmentRuntimeConfig;
    fitmenttool?: FitmentRuntimeConfig;
    forceFitmentSelector?: boolean;
};

export type CurrencyDisplayMode = 'off' | 'symbol' | 'postfix';

export interface SearchResponseSettings extends ProductSearchUiSettings {
    pricing_enabled?: boolean;
    uom_enabled?: boolean;
    quantity_breaks_enabled?: boolean;
    currency_symbol?: string;
    default_currency_symbol?: string;
    default_currency_code?: string;
    currency_display_mode?: CurrencyDisplayMode;
    show_currency_code?: boolean;
    tax_display_mode?: string;
    prices_include_tax?: boolean;
    price_display_tax_enabled?: boolean;
    price_display_multiplier?: number;
    price_including_tax_multiplier?: number;
    price_excluding_tax_multiplier?: number;
    price_display_suffix?: string;
    price_display_suffix_template?: string;
    favourites_enabled?: boolean;
    show_favourite_button?: boolean;
    favourites_can_toggle?: boolean;
    favourites_ajax_url?: string;
    favourites_nonce?: string;
    favourites_groups_enabled?: boolean;
    draft_orders_enabled?: boolean;
    draft_orders_can_manage?: boolean;
    can_create_drafts?: boolean;
    can_edit_draft_items?: boolean;
    quote_management_enabled?: boolean;
    can_create_quotes?: boolean;
    can_edit_quotes?: boolean;
    hide_calculated_price?: boolean;
    show_price?: boolean;
    allow_add_to_cart?: boolean;
    show_stock?: boolean;
    customer_warehouse_id?: string;
    warehouse_summary_full_display?: string;
    warehouse_summary_compact_display?: string;
    warehouse_summary_row_display?: string;
    back_in_stock_widget_enabled?: boolean;
    back_in_stock_b2b_enabled?: boolean;
    back_in_stock_button_label?: string;
    back_in_stock_success_message?: string;
    product_detail_base_path?: string;
    browse_products_url?: string;
}

export interface SearchResponse {
    total_pages: number;
    current_page: number;
    total_results: number;
    results: Product[];
    settings?: SearchResponseSettings;
}

type SelectedAttributeMap = Record<string, number[]>;

type SearchCriteria = {
    selectedCategoryIds: number[];
    selectedAttributes: SelectedAttributeMap;
    searchTerm: string;
    sortBy: string;
    page: number;
};

interface PersistedSearchState {
    endpoint: string;
    criteria: SearchCriteria;
    response: SearchResponse;
    timestamp: number;
    settingsSignature?: string;
}

type SearchRequestOptions = {
    bypassCache?: boolean;
    showLoading?: boolean;
};

const SEARCH_STATE_STORAGE_KEY = 'weka_b2b_find_products_state_v7';
const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const KEYWORD_SEARCH_DEBOUNCE_MS = 450;

const normalizeSelectedCategoryIds = (value: number[]): number[] => {
    return Array.from(
        new Set((value || [])
            .map((categoryId) => Number(categoryId))
            .filter((categoryId) => Number.isFinite(categoryId) && categoryId > 0))
    ).sort((a, b) => a - b);
};

const normalizeCategoryId = (value: unknown): number => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

const normalizeCategorySelection = (
    value: number[],
    allowMultipleCategories: boolean = true
): number[] => {
    if (allowMultipleCategories) {
        return normalizeSelectedCategoryIds(value);
    }

    for (const categoryId of Array.isArray(value) ? value : []) {
        const normalizedCategoryId = normalizeCategoryId(categoryId);
        if (normalizedCategoryId > 0) {
            return [normalizedCategoryId];
        }
    }

    return [];
};

const normalizeSelectedAttributes = (value: SelectedAttributeMap): SelectedAttributeMap => {
    const normalized: SelectedAttributeMap = {};
    for (const taxonomy of Object.keys(value).sort()) {
        const uniqueIds = Array.from(new Set((value[taxonomy] || [])
            .map((termId) => Number(termId))
            .filter((termId) => Number.isFinite(termId) && termId > 0)))
            .sort((a, b) => a - b);

        if (uniqueIds.length > 0) {
            normalized[taxonomy] = uniqueIds;
        }
    }

    return normalized;
};

const parseCategoryIdsFromQueryString = (queryString: string): number[] => {
    const params = new URLSearchParams(String(queryString || ''));
    const rawCategoryIds = params.get('categories') || '';
    if (rawCategoryIds.trim() === '') {
        return [];
    }

    const parsedIds: number[] = [];
    const seen = new Set<number>();
    rawCategoryIds.split(',').forEach((value) => {
        const categoryId = normalizeCategoryId(value);
        if (categoryId <= 0 || seen.has(categoryId)) {
            return;
        }

        seen.add(categoryId);
        parsedIds.push(categoryId);
    });

    return parsedIds;
};

const getDefaultSearchCriteria = (
    defaultCategoryId: number = 0,
    allowMultipleCategories: boolean = true
): SearchCriteria => ({
    selectedCategoryIds: normalizeCategorySelection(defaultCategoryId > 0 ? [defaultCategoryId] : [], allowMultipleCategories),
    selectedAttributes: {},
    searchTerm: '',
    sortBy: '',
    page: 1,
});

const applyDefaultCategoryOverride = (
    criteria: SearchCriteria,
    defaultCategoryId: number = 0,
    allowMultipleCategories: boolean = true
): SearchCriteria => {
    const normalizedCriteria = normalizeSearchCriteria(criteria, allowMultipleCategories);
    const defaultCategoryIds = normalizeCategorySelection(defaultCategoryId > 0 ? [defaultCategoryId] : [], allowMultipleCategories);

    if (normalizedCriteria.searchTerm !== '') {
        return {
            ...normalizedCriteria,
            selectedCategoryIds: [],
        };
    }

    if (defaultCategoryIds.length === 0) {
        return normalizedCriteria;
    }

    const currentCategoryIds = normalizeCategorySelection(normalizedCriteria.selectedCategoryIds, allowMultipleCategories);
    const shouldOverrideCategories = currentCategoryIds.length !== defaultCategoryIds.length
        || currentCategoryIds.some((categoryId, index) => categoryId !== defaultCategoryIds[index]);

    if (!shouldOverrideCategories) {
        return normalizedCriteria;
    }

    return {
        ...normalizedCriteria,
        selectedCategoryIds: defaultCategoryIds,
        page: 1,
    };
};

const normalizeSearchCriteria = (
    value: Partial<SearchCriteria>,
    allowMultipleCategories: boolean = true
): SearchCriteria => {
    const defaultCriteria = getDefaultSearchCriteria(0, allowMultipleCategories);
    const normalizedPage = Number(value.page ?? defaultCriteria.page);
    return {
        selectedCategoryIds: normalizeCategorySelection(value.selectedCategoryIds ?? defaultCriteria.selectedCategoryIds, allowMultipleCategories),
        selectedAttributes: normalizeSelectedAttributes(value.selectedAttributes ?? defaultCriteria.selectedAttributes),
        searchTerm: String(value.searchTerm ?? defaultCriteria.searchTerm).trim(),
        sortBy: String(value.sortBy ?? defaultCriteria.sortBy).trim(),
        page: Number.isFinite(normalizedPage) && normalizedPage > 0 ? Math.floor(normalizedPage) : 1,
    };
};

const parseAttributesFromQueryString = (queryString: string): SelectedAttributeMap => {
    const params = new URLSearchParams(String(queryString || ''));
    const rawAttributes = String(params.get('attributes') || '').trim();
    if (rawAttributes === '') {
        return {};
    }

    try {
        const decoded = JSON.parse(rawAttributes);
        if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
            return {};
        }

        const candidateAttributes: SelectedAttributeMap = {};
        Object.entries(decoded as Record<string, unknown>).forEach(([taxonomy, values]) => {
            const safeTaxonomy = String(taxonomy || '').trim();
            if (safeTaxonomy === '') {
                return;
            }

            const entries = Array.isArray(values) ? values : [values];
            candidateAttributes[safeTaxonomy] = entries
                .map((entry) => Number(entry))
                .filter((entry) => Number.isFinite(entry) && entry > 0);
        });

        return normalizeSelectedAttributes(candidateAttributes);
    } catch {
        return {};
    }
};

const parseSearchCriteriaFromQueryString = (
    queryString: string,
    allowMultipleCategories: boolean = true
): SearchCriteria => {
    const params = new URLSearchParams(String(queryString || ''));
    const rawPage = Number(params.get('page') || 1);
    return normalizeSearchCriteria({
        selectedCategoryIds: parseCategoryIdsFromQueryString(queryString),
        selectedAttributes: parseAttributesFromQueryString(queryString),
        searchTerm: String(params.get('query') || '').trim(),
        sortBy: String(params.get('sort') || '').trim(),
        page: Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 1,
    }, allowMultipleCategories);
};

const buildApiSearchParams = (criteria: SearchCriteria): URLSearchParams => {
    const params = new URLSearchParams();
    const hasSearchTerm = criteria.searchTerm !== '';

    if (!hasSearchTerm && criteria.selectedCategoryIds.length > 0) {
        params.set('categories', criteria.selectedCategoryIds.join(','));
    }

    if (Object.keys(criteria.selectedAttributes).length > 0) {
        params.set('attributes', JSON.stringify(criteria.selectedAttributes));
    }

    if (hasSearchTerm) {
        params.set('query', criteria.searchTerm);
    }

    if (criteria.sortBy !== '') {
        params.set('sort', criteria.sortBy);
    }

    params.set('page', String(criteria.page));
    return params;
};

const buildRouteSearchParams = (criteria: SearchCriteria): URLSearchParams => {
    const params = new URLSearchParams();
    const hasSearchTerm = criteria.searchTerm !== '';

    if (!hasSearchTerm && criteria.selectedCategoryIds.length > 0) {
        params.set('categories', criteria.selectedCategoryIds.join(','));
    }

    if (Object.keys(criteria.selectedAttributes).length > 0) {
        params.set('attributes', JSON.stringify(criteria.selectedAttributes));
    }

    if (hasSearchTerm) {
        params.set('query', criteria.searchTerm);
    }

    if (criteria.sortBy !== '') {
        params.set('sort', criteria.sortBy);
    }

    if (criteria.page > 1) {
        params.set('page', String(criteria.page));
    }

    return params;
};

const buildSearchCacheKey = (criteria: SearchCriteria): string => {
    return buildApiSearchParams(criteria).toString();
};

const areSearchCriteriaEqual = (
    left: SearchCriteria,
    right: SearchCriteria,
    allowMultipleCategories: boolean = true
): boolean => {
    return JSON.stringify(normalizeSearchCriteria(left, allowMultipleCategories))
        === JSON.stringify(normalizeSearchCriteria(right, allowMultipleCategories));
};

const buildPersistedStateSettingsSignature = (uiSettings: ProductSearchUiSettings): string => {
    const normalizedUiSettings = normalizeProductSearchUiSettings(uiSettings);

    return JSON.stringify({
        header_title: String(normalizedUiSettings.header_title || '').trim(),
        header_subtitle: String(normalizedUiSettings.header_subtitle || '').trim(),
        hide_header_subtitle: normalizedUiSettings.hide_header_subtitle === true,
        search_placeholder: String(normalizedUiSettings.search_placeholder || '').trim(),
        persistent_category_sidebar: normalizedUiSettings.persistent_category_sidebar === true,
        desktop_collapsible_category_sidebar: normalizedUiSettings.desktop_collapsible_category_sidebar === true,
        category_tree_collapsed_by_default: normalizedUiSettings.category_tree_collapsed_by_default === true,
        auto_apply_category_filters: normalizedUiSettings.auto_apply_category_filters === true,
        default_category_id: normalizeCategoryId(normalizedUiSettings.default_category_id),
        category_filter_display: String(normalizedUiSettings.category_filter_display || 'tree').trim().toLowerCase() === 'dropdown'
            ? 'dropdown'
            : 'tree',
        debounce_keyword_search: normalizedUiSettings.debounce_keyword_search === true,
        show_upload_csv: normalizedUiSettings.show_upload_csv !== false,
        show_add_all_button: normalizedUiSettings.show_add_all_button !== false,
        show_attribute_filters: normalizedUiSettings.show_attribute_filters !== false,
        show_product_image: normalizedUiSettings.show_product_image !== false,
        show_product_name: normalizedUiSettings.show_product_name !== false,
        show_product_description: normalizedUiSettings.show_product_description !== false,
        rrp_source: String(normalizedUiSettings.rrp_source || '').trim().toLowerCase(),
        rrp_meta_key: String(normalizedUiSettings.rrp_meta_key || '').trim(),
        show_rrp_strikethrough: normalizedUiSettings.show_rrp_strikethrough !== false,
        allow_multiple_categories: normalizedUiSettings.allow_multiple_categories !== false,
        use_woocommerce_product_links: normalizedUiSettings.use_woocommerce_product_links === true,
        show_backorder_label_on_add_to_cart: normalizedUiSettings.show_backorder_label_on_add_to_cart === true,
        visible_columns: normalizeVisibleColumns(normalizedUiSettings.visible_columns || []),
    });
};

const loadPersistedSearchState = (
    searchEndpoint: string,
    urlCriteria: SearchCriteria,
    allowMultipleCategories: boolean = true,
    settingsSignature: string = ''
): PersistedSearchState | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const raw = window.sessionStorage.getItem(SEARCH_STATE_STORAGE_KEY);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as PersistedSearchState;
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }

        if (String(parsed.endpoint || '') !== searchEndpoint) {
            return null;
        }

        if (String(parsed.settingsSignature || '') !== settingsSignature) {
            window.sessionStorage.removeItem(SEARCH_STATE_STORAGE_KEY);
            return null;
        }

        const timestamp = Number(parsed.timestamp || 0);
        if (!Number.isFinite(timestamp) || timestamp <= 0 || (Date.now() - timestamp) > SEARCH_CACHE_TTL_MS) {
            return null;
        }

        if (!parsed.response || typeof parsed.response !== 'object' || !Array.isArray(parsed.response.results)) {
            return null;
        }

        const normalizedPersistedCriteria = normalizeSearchCriteria(parsed.criteria || {}, allowMultipleCategories);
        if (allowMultipleCategories === false && normalizedPersistedCriteria.selectedCategoryIds.length !== Number((parsed.criteria?.selectedCategoryIds || []).length || 0)) {
            return null;
        }

        const hasRouteParams = String(window.location.search || '').trim() !== '';
        if (hasRouteParams && !areSearchCriteriaEqual(normalizedPersistedCriteria, urlCriteria, allowMultipleCategories)) {
            return null;
        }

        return {
            endpoint: searchEndpoint,
            criteria: normalizedPersistedCriteria,
            response: parsed.response,
            timestamp,
        };
    } catch {
        return null;
    }
};

const persistSearchState = (payload: PersistedSearchState): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.setItem(SEARCH_STATE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // No-op when storage is unavailable.
    }
};

const filterCategoryTreeNodes = (nodes: CategoryTreeNode[], normalizedSearch: string): CategoryTreeNode[] => {
    if (normalizedSearch === '') {
        return nodes;
    }

    const filtered: CategoryTreeNode[] = [];
    for (const node of nodes) {
        const nodeName = String(node.name || '');
        const matches = nodeName.toLowerCase().includes(normalizedSearch);
        const filteredChildren = filterCategoryTreeNodes(node.children || [], normalizedSearch);
        if (matches || filteredChildren.length > 0) {
            filtered.push({
                ...node,
                children: filteredChildren,
            });
        }
    }

    return filtered;
};

const buildCategoryLookup = (
    nodes: CategoryTreeNode[],
    lookup: Map<number, {name: string; count: number | null}> = new Map()
): Map<number, {name: string; count: number | null}> => {
    for (const node of nodes || []) {
        const categoryId = normalizeCategoryId(node.id);
        if (categoryId > 0) {
            const parsedCount = Number(node.count);
            lookup.set(categoryId, {
                name: String(node.name || '').trim(),
                count: Number.isFinite(parsedCount) ? Math.max(0, parsedCount) : null,
            });
        }

        if (Array.isArray(node.children) && node.children.length > 0) {
            buildCategoryLookup(node.children, lookup);
        }
    }

    return lookup;
};

const normalizeVisibleColumns = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return Array.from(new Set(value
        .map((entry) => String(entry || '').trim().toLowerCase())
        .filter((entry) => entry !== ''))).sort();
};

const normalizeBooleanSetting = (value: unknown, defaultValue: boolean = false): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    const normalizedValue = String(value || '').trim().toLowerCase();
    if (normalizedValue === '') {
        return defaultValue;
    }

    if (['1', 'true', 'yes', 'on'].includes(normalizedValue)) {
        return true;
    }

    if (['0', 'false', 'no', 'off'].includes(normalizedValue)) {
        return false;
    }

    return defaultValue;
};

const normalizeProductSearchUiSettings = (value?: ProductSearchUiSettings | SearchResponseSettings): ProductSearchUiSettings => {
    const source = value && typeof value === 'object' ? value : {};
    const headerTitle = String(source.header_title || '').trim();
    const headerSubtitle = String(source.header_subtitle || '').trim();
    const searchPlaceholder = String(source.search_placeholder || '').trim();

    const normalizedSettings: ProductSearchUiSettings = {
        header_title: headerTitle !== '' ? headerTitle : 'Find Products',
        header_subtitle: headerSubtitle,
        hide_header_subtitle: normalizeBooleanSetting(source.hide_header_subtitle, false),
        search_placeholder: searchPlaceholder,
        persistent_category_sidebar: normalizeBooleanSetting(source.persistent_category_sidebar, false),
        desktop_collapsible_category_sidebar: normalizeBooleanSetting(source.desktop_collapsible_category_sidebar, false),
        category_tree_collapsed_by_default: normalizeBooleanSetting(source.category_tree_collapsed_by_default, false),
        auto_apply_category_filters: normalizeBooleanSetting(source.auto_apply_category_filters, false),
        default_category_id: normalizeCategoryId(source.default_category_id),
        category_filter_display: String(source.category_filter_display || 'tree').trim().toLowerCase() === 'dropdown'
            ? 'dropdown'
            : 'tree',
        debounce_keyword_search: normalizeBooleanSetting(source.debounce_keyword_search, false),
        show_upload_csv: normalizeBooleanSetting(source.show_upload_csv, true),
        show_add_all_button: normalizeBooleanSetting(source.show_add_all_button, true),
        show_attribute_filters: normalizeBooleanSetting(source.show_attribute_filters, true),
        show_product_image: normalizeBooleanSetting(source.show_product_image, true),
        show_product_name: normalizeBooleanSetting(source.show_product_name, true),
        show_product_description: normalizeBooleanSetting(source.show_product_description, true),
        rrp_source: String(source.rrp_source || '').trim().toLowerCase(),
        rrp_meta_key: String(source.rrp_meta_key || '').trim(),
        show_rrp_strikethrough: normalizeBooleanSetting(source.show_rrp_strikethrough, true),
        allow_multiple_categories: normalizeBooleanSetting(source.allow_multiple_categories, true),
        use_woocommerce_product_links: normalizeBooleanSetting(source.use_woocommerce_product_links, false),
        show_backorder_label_on_add_to_cart: normalizeBooleanSetting(source.show_backorder_label_on_add_to_cart, false),
    };

    if (Array.isArray(source.visible_columns)) {
        normalizedSettings.visible_columns = normalizeVisibleColumns(source.visible_columns);
    }

    return normalizedSettings;
};

const buildCategoryParentLookup = (
    nodes: CategoryTreeNode[],
    lookup: Map<number, number> = new Map()
): Map<number, number> => {
    for (const node of nodes || []) {
        const categoryId = normalizeCategoryId(node.id);
        if (categoryId <= 0) {
            continue;
        }

        lookup.set(categoryId, normalizeCategoryId(node.parent));
        if (Array.isArray(node.children) && node.children.length > 0) {
            buildCategoryParentLookup(node.children, lookup);
        }
    }

    return lookup;
};

const flattenCategoryTreeNodes = (
    nodes: CategoryTreeNode[],
    depth: number = 0,
    flattened: Array<{id: number; name: string; depth: number; count: number | null}> = []
): Array<{id: number; name: string; depth: number; count: number | null}> => {
    for (const node of nodes || []) {
        const categoryId = normalizeCategoryId(node.id);
        if (categoryId <= 0) {
            continue;
        }

        const parsedCount = Number(node.count);
        flattened.push({
            id: categoryId,
            name: String(node.name || '').trim(),
            depth,
            count: Number.isFinite(parsedCount) && parsedCount >= 0 ? parsedCount : null,
        });

        if (Array.isArray(node.children) && node.children.length > 0) {
            flattenCategoryTreeNodes(node.children, depth + 1, flattened);
        }
    }

    return flattened;
};

const buildExpandedCategoryAncestorSet = (
    categoryIds: number[],
    parentLookup: Map<number, number>
): Set<number> => {
    const expanded = new Set<number>();

    categoryIds.forEach((categoryId) => {
        let currentId = normalizeCategoryId(categoryId);
        while (currentId > 0) {
            const parentId = normalizeCategoryId(parentLookup.get(currentId));
            if (parentId <= 0) {
                break;
            }

            expanded.add(parentId);
            currentId = parentId;
        }
    });

    return expanded;
};

const buildTreeFromLegacyCategories = (
    categories?: {main: Category[]; sub: Category[]; sub2: Category[]}
): CategoryTreeNode[] => {
    if (!categories) {
        return [];
    }

    const allCategories = [
        ...(Array.isArray(categories.main) ? categories.main : []),
        ...(Array.isArray(categories.sub) ? categories.sub : []),
        ...(Array.isArray(categories.sub2) ? categories.sub2 : []),
    ];

    const nodeMap = new Map<number, CategoryTreeNode>();
    for (const category of allCategories) {
        const id = Number(category.term_id || category.cat_ID || 0);
        if (!Number.isFinite(id) || id <= 0) {
            continue;
        }

        const parent = Number(category.parent ?? category.category_parent ?? 0);
        nodeMap.set(id, {
            id,
            name: String(category.name || '').trim(),
            parent: Number.isFinite(parent) ? parent : 0,
            count: Number.isFinite(Number(category.count)) ? Number(category.count) : 0,
            children: [],
        });
    }

    const roots: CategoryTreeNode[] = [];
    for (const node of nodeMap.values()) {
        const parentId = normalizeCategoryId(node.parent);
        if (parentId > 0 && nodeMap.has(parentId)) {
            const parent = nodeMap.get(parentId)!;
            parent.children = [...(parent.children || []), node];
        } else {
            roots.push(node);
        }
    }

    const sortNodes = (nodes: CategoryTreeNode[]): CategoryTreeNode[] => {
        return [...nodes]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((node) => ({
                ...node,
                children: node.children && node.children.length > 0
                    ? sortNodes(node.children)
                    : [],
            }));
    };

    return sortNodes(roots);
};

const ProductSearchForm: React.FC<ProductSearchFormProps> = ({
    categories: propCategories,
    categoryOptions: propCategoryOptions,
    attributes: propAttributes,
    client_endpoint,
    templateCsvUrl,
    uiSettings: propUiSettings,
    uiSettingsOverride,
    fitmentTool,
    fitmenttool,
    forceFitmentSelector = false,
}) => {
    const fitmentToolConfig = fitmentTool || fitmenttool;
    const showInlineFitmentSelector = forceFitmentSelector || fitmentToolConfig?.display_mode !== 'page';
    const initialCategoryOptions = useMemo(() => {
        if (Array.isArray(propCategoryOptions) && propCategoryOptions.length > 0) {
            return propCategoryOptions;
        }

        return buildTreeFromLegacyCategories(propCategories);
    }, [propCategoryOptions, propCategories]);

    const initialAttributeFilters = useMemo(() => {
        if (!Array.isArray(propAttributes)) {
            return [];
        }

        return propAttributes.filter((attribute) => (
            attribute
            && typeof attribute.taxonomy === 'string'
            && typeof attribute.label === 'string'
            && Array.isArray(attribute.terms)
        ));
    }, [propAttributes]);

    const initialUiSettings = useMemo(() => (
        normalizeProductSearchUiSettings({
            ...(propUiSettings || {}),
            ...(uiSettingsOverride || {}),
        })
    ), [propUiSettings, uiSettingsOverride]);

    const {get} = useApi();
    const searchEndpoint = (() => {
        const raw = String(client_endpoint || '').trim();
        if (!raw) {
            return '/search';
        }
        return raw.startsWith('/') ? raw : `/${raw}`;
    })();
    const filtersEndpoint = `${searchEndpoint.replace(/\/$/, '')}/filters`;

    const initialHydratedState = useMemo(() => {
        const allowMultipleCategories = initialUiSettings.allow_multiple_categories !== false;
        const defaultCategoryId = Number(initialUiSettings.default_category_id || 0);
        const settingsSignature = buildPersistedStateSettingsSignature(initialUiSettings);
        const defaultCriteria = getDefaultSearchCriteria(defaultCategoryId, allowMultipleCategories);
        if (typeof window === 'undefined') {
            return {
                criteria: defaultCriteria,
                response: null,
                timestamp: 0,
            };
        }

        const rawLocationSearch = String(window.location.search || '');
        const hasRouteParams = rawLocationSearch.trim() !== '';
        const urlCriteria = hasRouteParams
            ? parseSearchCriteriaFromQueryString(rawLocationSearch, allowMultipleCategories)
            : defaultCriteria;
        const persistedState = loadPersistedSearchState(searchEndpoint, urlCriteria, allowMultipleCategories, settingsSignature);
        const baseCriteria = persistedState?.criteria ?? urlCriteria;
        const hydratedCriteria = applyDefaultCategoryOverride(baseCriteria, defaultCategoryId, allowMultipleCategories);
        const canHydrateResponse = Boolean(
            persistedState?.response
            && areSearchCriteriaEqual(persistedState.criteria, hydratedCriteria, allowMultipleCategories)
        );

        return {
            criteria: hydratedCriteria,
            response: canHydrateResponse ? persistedState?.response ?? null : null,
            timestamp: canHydrateResponse ? persistedState?.timestamp ?? 0 : 0,
        };
    }, [initialUiSettings.allow_multiple_categories, initialUiSettings.default_category_id, searchEndpoint]);

    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(initialHydratedState.criteria.selectedCategoryIds);
    const [selectedAttributes, setSelectedAttributes] = useState<SelectedAttributeMap>(initialHydratedState.criteria.selectedAttributes);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [expandedCategoryNodes, setExpandedCategoryNodes] = useState<Set<number>>(new Set());
    const [attributeSearchMap, setAttributeSearchMap] = useState<Record<string, string>>({});
    const [searchTerm, setSearchTerm] = useState(initialHydratedState.criteria.searchTerm);
    const [isFiltersVisible, setFiltersVisible] = useState(false);
    const [isDesktopCategorySidebarCollapsed, setIsDesktopCategorySidebarCollapsed] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(initialHydratedState.response === null);
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false);
    const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(initialHydratedState.response);
    const [hasFreshSearchResponse, setHasFreshSearchResponse] = useState(false);
    const [hasSearched, setHasSearched] = useState(Boolean(initialHydratedState.response));
    const [sortBy, setSortBy] = useState(initialHydratedState.criteria.sortBy);
    const [page, setPage] = useState(initialHydratedState.criteria.page);
    const [categoryOptions, setCategoryOptions] = useState<CategoryTreeNode[]>(initialCategoryOptions);
    const [attributeFilters, setAttributeFilters] = useState<ProductAttributeFilter[]>(initialAttributeFilters);
    const hasLoadedFilters = useRef(false);
    const hasInitializedCategoryTree = useRef(false);
    const appliedDefaultCategoryOverrideKeyRef = useRef<string>('');
    const userExpandedCategoryNodesRef = useRef<Set<number>>(new Set());
    const hasInitializedKeywordDebounce = useRef(false);

    const searchCache = useRef<Map<string, { data: SearchResponse; timestamp: number }>>(new Map());
    const skipNextAutomaticSearch = useRef(Boolean(initialHydratedState.response));
    const shouldRevalidateHydratedState = useRef(Boolean(initialHydratedState.response));
    const latestSearchRequestId = useRef(0);

    const effectiveUiSettings = useMemo(() => {
        if (hasFreshSearchResponse && searchResponse?.settings) {
            return normalizeProductSearchUiSettings({
                ...searchResponse.settings,
                ...(uiSettingsOverride || {}),
            });
        }

        return normalizeProductSearchUiSettings({
            ...(searchResponse?.settings || {}),
            ...initialUiSettings,
        });
    }, [hasFreshSearchResponse, initialUiSettings, searchResponse?.settings, uiSettingsOverride]);

    const headerTitle = String(effectiveUiSettings.header_title || 'Find Products').trim() || 'Find Products';
    const customSubtitle = String(effectiveUiSettings.header_subtitle || '').trim();
    const showAttributeFilters = effectiveUiSettings.show_attribute_filters !== false;
    const showCategorySidebar = effectiveUiSettings.persistent_category_sidebar === true;
    const autoApplyCategoryFilters = effectiveUiSettings.auto_apply_category_filters === true;
    const allowMultipleCategories = effectiveUiSettings.allow_multiple_categories !== false;
    const categoryFilterDisplay = effectiveUiSettings.category_filter_display === 'dropdown' ? 'dropdown' : 'tree';
    const debounceKeywordSearch = effectiveUiSettings.debounce_keyword_search === true;
    const defaultSubtitle = showAttributeFilters
        ? 'Search by item code, product name, category, or attribute.'
        : 'Search by item code, product name, or category.';
    const pageSubtitle = effectiveUiSettings.hide_header_subtitle === true
        ? undefined
        : (customSubtitle !== '' ? customSubtitle : defaultSubtitle);
    const searchPlaceholder = String(effectiveUiSettings.search_placeholder || '').trim() || 'Enter item code or product name...';
    const fitmentAttributes = useMemo(() => (
        fitmentToolConfig?.enabled === true && Array.isArray(fitmentToolConfig.attributes)
            ? fitmentToolConfig.attributes
                .map((attribute) => ({
                    taxonomy: String(attribute.taxonomy || '').trim(),
                    label: String(attribute.label || '').trim(),
                }))
                .filter((attribute) => attribute.taxonomy !== '' && attribute.label !== '')
            : []
    ), [fitmentToolConfig]);

    useEffect(() => {
        setCategoryOptions(initialCategoryOptions);
    }, [initialCategoryOptions]);

    useEffect(() => {
        setAttributeFilters(initialAttributeFilters);
    }, [initialAttributeFilters]);

    const selectedCategoryIdsNormalized = useMemo(() => (
        normalizeCategorySelection(selectedCategoryIds, allowMultipleCategories)
    ), [allowMultipleCategories, selectedCategoryIds]);

    const selectedAttributesNormalized = useMemo(() => (
        normalizeSelectedAttributes(selectedAttributes)
    ), [selectedAttributes]);

    const selectedFitmentAttributes = useMemo(() => {
        const nextSelection: Record<string, number> = {};
        fitmentAttributes.forEach((attribute) => {
            const termId = Number(selectedAttributesNormalized[attribute.taxonomy]?.[0] || 0);
            if (Number.isFinite(termId) && termId > 0) {
                nextSelection[attribute.taxonomy] = termId;
            }
        });

        return nextSelection;
    }, [fitmentAttributes, selectedAttributesNormalized]);

    const selectedCategorySet = useMemo(() => (
        new Set(selectedCategoryIdsNormalized)
    ), [selectedCategoryIdsNormalized]);

    const categoryLookup = useMemo(() => (
        buildCategoryLookup(categoryOptions)
    ), [categoryOptions]);

    const categoryParentLookup = useMemo(() => (
        buildCategoryParentLookup(categoryOptions)
    ), [categoryOptions]);

    const normalizedCategorySearch = categorySearchTerm.trim().toLowerCase();

    const visibleCategoryOptions = useMemo(() => {
        return filterCategoryTreeNodes(categoryOptions, normalizedCategorySearch);
    }, [categoryOptions, normalizedCategorySearch]);

    const flatVisibleCategoryOptions = useMemo(() => (
        flattenCategoryTreeNodes(visibleCategoryOptions)
    ), [visibleCategoryOptions]);

    const flatCategoryOptions = useMemo(() => (
        flattenCategoryTreeNodes(categoryOptions)
    ), [categoryOptions]);

    const currentCriteria = useMemo(() => (
        normalizeSearchCriteria({
            selectedCategoryIds: selectedCategoryIdsNormalized,
            selectedAttributes: selectedAttributesNormalized,
            searchTerm,
            sortBy,
            page,
        }, allowMultipleCategories)
    ), [allowMultipleCategories, selectedCategoryIdsNormalized, selectedAttributesNormalized, searchTerm, sortBy, page]);

    useEffect(() => {
        if (searchTerm.trim() === '' || selectedCategoryIdsNormalized.length === 0) {
            return;
        }

        setSelectedCategoryIds([]);
        setCategorySearchTerm('');
    }, [searchTerm, selectedCategoryIdsNormalized]);

    useEffect(() => {
        if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) {
            return;
        }

        const selectedAncestorIds = buildExpandedCategoryAncestorSet(selectedCategoryIdsNormalized, categoryParentLookup);
        setExpandedCategoryNodes((previous) => {
            let next = new Set(previous);
            if (!hasInitializedCategoryTree.current) {
                hasInitializedCategoryTree.current = true;
                next.clear();
                if (effectiveUiSettings.category_tree_collapsed_by_default !== true) {
                    categoryOptions.forEach((node) => {
                        const categoryId = normalizeCategoryId(node.id);
                        if (categoryId > 0) {
                            next.add(categoryId);
                        }
                    });
                }
            }

            if (effectiveUiSettings.category_tree_collapsed_by_default === true) {
                next = new Set(userExpandedCategoryNodesRef.current);
            }

            selectedAncestorIds.forEach((categoryId) => {
                next.add(categoryId);
            });
            return next;
        });
    }, [categoryOptions, categoryParentLookup, effectiveUiSettings.category_tree_collapsed_by_default, selectedCategoryIdsNormalized]);

    useEffect(() => {
        const shouldLoadCategories = initialCategoryOptions.length === 0;
        const shouldLoadAttributes = showAttributeFilters && initialAttributeFilters.length === 0;
        if (hasLoadedFilters.current || (!shouldLoadCategories && !shouldLoadAttributes)) {
            return;
        }

        hasLoadedFilters.current = true;
        setIsFiltersLoading(true);
        get<SearchFiltersResponse>(filtersEndpoint)
            .then((payload) => {
                if (Array.isArray(payload.categories)) {
                    setCategoryOptions(payload.categories);
                }

                if (showAttributeFilters && Array.isArray(payload.attributes)) {
                    setAttributeFilters(payload.attributes);
                }
            })
            .catch((error) => {
                console.error('Failed to load product filters:', error);
                toast.error('Unable to load product filters.');
            })
            .finally(() => {
                setIsFiltersLoading(false);
            });
    }, [filtersEndpoint, get, initialAttributeFilters.length, initialCategoryOptions.length, showAttributeFilters]);

    const scrollToTopOfResults = (behavior: ScrollBehavior = 'smooth') => {
        if (typeof document === 'undefined') {
            return;
        }

        const container = document.getElementById('weka-content');
        if (container) {
            container.scrollIntoView({behavior, block: 'start'});
            return;
        }

        window.scrollTo({top: 0, behavior});
    };

    useEffect(() => {
        if (!initialHydratedState.response) {
            return;
        }

        const initialCacheKey = buildSearchCacheKey(initialHydratedState.criteria);
        searchCache.current.set(initialCacheKey, {
            data: initialHydratedState.response,
            timestamp: initialHydratedState.timestamp || Date.now(),
        });
    }, [initialHydratedState]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const nextRouteSearch = buildRouteSearchParams(currentCriteria).toString();
        const currentRouteSearch = new URLSearchParams(String(window.location.search || '')).toString();
        if (nextRouteSearch === currentRouteSearch) {
            return;
        }

        const nextUrl = `${window.location.pathname}${nextRouteSearch !== '' ? `?${nextRouteSearch}` : ''}`;
        window.history.replaceState(window.history.state, '', nextUrl);
    }, [currentCriteria]);

    const toggleCategoryExpanded = (categoryId: number) => {
        setExpandedCategoryNodes((previous) => {
            const next = new Set(previous);
            const nextUserExpanded = new Set(userExpandedCategoryNodesRef.current);
            if (next.has(categoryId)) {
                next.delete(categoryId);
                nextUserExpanded.delete(categoryId);
            } else {
                next.add(categoryId);
                nextUserExpanded.add(categoryId);
            }

            userExpandedCategoryNodesRef.current = nextUserExpanded;
            return next;
        });
    };

    const doSearch = (
        overrides: Partial<SearchCriteria> = {},
        options: SearchRequestOptions = {}
    ) => {
        const {bypassCache = false, showLoading = true} = options;
        const effectiveCriteria = normalizeSearchCriteria({
            selectedCategoryIds: overrides.selectedCategoryIds ?? selectedCategoryIdsNormalized,
            selectedAttributes: overrides.selectedAttributes ?? selectedAttributesNormalized,
            searchTerm: overrides.searchTerm ?? searchTerm,
            sortBy: overrides.sortBy ?? sortBy,
            page: overrides.page ?? page,
        }, allowMultipleCategories);
        const searchParams = buildApiSearchParams(effectiveCriteria);
        const cacheKey = buildSearchCacheKey(effectiveCriteria);
        const searchRequestId = latestSearchRequestId.current + 1;
        latestSearchRequestId.current = searchRequestId;

        if (!bypassCache) {
            const cached = searchCache.current.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp < SEARCH_CACHE_TTL_MS)) {
                setSearchResponse(cached.data);
                setHasSearched(true);
                setIsLoading(false);
                persistSearchState({
                    endpoint: searchEndpoint,
                    criteria: effectiveCriteria,
                    response: cached.data,
                    timestamp: Date.now(),
                    settingsSignature: buildPersistedStateSettingsSignature(cached.data.settings || initialUiSettings),
                });
                return;
            }
        }

        if (showLoading) {
            setIsLoading(true);
        }

        get<SearchResponse>(`${searchEndpoint}?${searchParams.toString()}`).then((res) => {
            if (searchRequestId !== latestSearchRequestId.current) {
                return;
            }

            setSearchResponse(res);
            setHasFreshSearchResponse(true);

            searchCache.current.set(cacheKey, {
                data: res,
                timestamp: Date.now(),
            });

            persistSearchState({
                endpoint: searchEndpoint,
                criteria: effectiveCriteria,
                response: res,
                timestamp: Date.now(),
                settingsSignature: buildPersistedStateSettingsSignature(res.settings || initialUiSettings),
            });

            if (searchCache.current.size > 20) {
                const firstKey = searchCache.current.keys().next().value;
                if (typeof firstKey === 'string') {
                    searchCache.current.delete(firstKey);
                }
            }
        }).catch((err) => {
            if (searchRequestId !== latestSearchRequestId.current) {
                return;
            }

            console.error(err);
            toast.error('We are having trouble fetching any data.');
        }).finally(() => {
            if (searchRequestId !== latestSearchRequestId.current) {
                return;
            }

            if (showLoading) {
                setIsLoading(false);
            }
            setHasSearched(true);
        });
    };

    const refreshFiltersForAttributes = (nextAttributes: SelectedAttributeMap) => {
        const params = new URLSearchParams();
        const normalizedAttributes = normalizeSelectedAttributes(nextAttributes);
        if (Object.keys(normalizedAttributes).length > 0) {
            params.set('attributes', JSON.stringify(normalizedAttributes));
        }

        const endpoint = `${filtersEndpoint}${params.toString() !== '' ? `?${params.toString()}` : ''}`;
        setIsFiltersLoading(true);
        get<SearchFiltersResponse>(endpoint)
            .then((payload) => {
                if (Array.isArray(payload.categories)) {
                    setCategoryOptions(payload.categories);
                }

                if (showAttributeFilters && Array.isArray(payload.attributes)) {
                    setAttributeFilters(payload.attributes);
                }
            })
            .catch((error) => {
                console.error('Failed to refresh product filters:', error);
                toast.error('Unable to refresh product filters.');
            })
            .finally(() => setIsFiltersLoading(false));
    };

    const applyFitmentSelection = (fitmentSelection: Record<string, number>, applySearch: boolean) => {
        const fitmentTaxonomies = new Set(fitmentAttributes.map((attribute) => attribute.taxonomy));
        const nextAttributes: SelectedAttributeMap = {};

        Object.entries(selectedAttributesNormalized).forEach(([taxonomy, termIds]) => {
            if (!fitmentTaxonomies.has(taxonomy)) {
                nextAttributes[taxonomy] = termIds;
            }
        });

        fitmentAttributes.forEach((attribute) => {
            const termId = Number(fitmentSelection[attribute.taxonomy] || 0);
            if (Number.isFinite(termId) && termId > 0) {
                nextAttributes[attribute.taxonomy] = [Math.floor(termId)];
            }
        });

        const normalizedAttributes = normalizeSelectedAttributes(nextAttributes);
        setSelectedAttributes(normalizedAttributes);
        setSelectedCategoryIds([]);
        setCategorySearchTerm('');
        setPage(1);
        refreshFiltersForAttributes(normalizedAttributes);

        if (applySearch) {
            doSearch({
                selectedAttributes: normalizedAttributes,
                selectedCategoryIds: [],
                page: 1,
            });
            setFiltersVisible(false);
        }
    };

    const runSearchWithPageReset = (overrides: Partial<SearchCriteria> = {}, closeFilters: boolean = true) => {
        const nextPage = 1;
        setPage(nextPage);
        doSearch({
            ...overrides,
            page: nextPage,
        });
        if (closeFilters) {
            setFiltersVisible(false);
        }
    };

    useEffect(() => {
        if (!debounceKeywordSearch) {
            hasInitializedKeywordDebounce.current = false;
            return;
        }

        if (!hasInitializedKeywordDebounce.current) {
            hasInitializedKeywordDebounce.current = true;
            return;
        }

        const timer = window.setTimeout(() => {
            const clearedCategoryIds: number[] = [];
            setSelectedCategoryIds(clearedCategoryIds);
            setCategorySearchTerm('');
            runSearchWithPageReset({
                searchTerm: searchTerm.trim(),
                selectedCategoryIds: clearedCategoryIds,
            }, false);
        }, KEYWORD_SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timer);
    }, [debounceKeywordSearch, searchTerm]);

    useEffect(() => {
        const defaultCategoryId = normalizeCategoryId(effectiveUiSettings.default_category_id);
        if (defaultCategoryId <= 0) {
            return;
        }

        const overrideKey = JSON.stringify({
            endpoint: searchEndpoint,
            defaultCategoryId,
            allowMultipleCategories,
        });
        if (searchTerm.trim() !== '') {
            appliedDefaultCategoryOverrideKeyRef.current = overrideKey;
            return;
        }

        if (appliedDefaultCategoryOverrideKeyRef.current === overrideKey) {
            return;
        }

        const defaultCategoryIds = normalizeCategorySelection([defaultCategoryId], allowMultipleCategories);
        const alreadyMatchesDefault = selectedCategoryIdsNormalized.length === defaultCategoryIds.length
            && selectedCategoryIdsNormalized.every((categoryId, index) => categoryId === defaultCategoryIds[index]);

        appliedDefaultCategoryOverrideKeyRef.current = overrideKey;
        if (alreadyMatchesDefault) {
            return;
        }

        skipNextAutomaticSearch.current = page !== 1;
        shouldRevalidateHydratedState.current = false;
        setSelectedCategoryIds(defaultCategoryIds);
        runSearchWithPageReset({selectedCategoryIds: defaultCategoryIds}, false);
    }, [
        allowMultipleCategories,
        effectiveUiSettings.default_category_id,
        page,
        searchEndpoint,
        searchTerm,
        selectedCategoryIdsNormalized,
    ]);

    const updateSelectedCategories = (nextCategoryIds: number[], applySearch: boolean = autoApplyCategoryFilters) => {
        const normalizedCategoryIds = normalizeCategorySelection(nextCategoryIds, allowMultipleCategories);
        setSelectedCategoryIds(normalizedCategoryIds);
        if (applySearch) {
            runSearchWithPageReset({selectedCategoryIds: normalizedCategoryIds}, false);
        }
    };

    const clearSelectedCategories = (applySearch: boolean = autoApplyCategoryFilters) => {
        setCategorySearchTerm('');
        updateSelectedCategories([], applySearch);
    };

    const toggleCategorySelection = (categoryId: number) => {
        const safeCategoryId = Number(categoryId || 0);
        if (!Number.isFinite(safeCategoryId) || safeCategoryId <= 0) {
            return;
        }

        const nextCategoryIds = selectedCategorySet.has(safeCategoryId)
            ? selectedCategoryIdsNormalized.filter((id) => id !== safeCategoryId)
            : (allowMultipleCategories ? [...selectedCategoryIdsNormalized, safeCategoryId] : [safeCategoryId]);
        updateSelectedCategories(nextCategoryIds, autoApplyCategoryFilters);
    };

    const onDropdownCategoryChange = (rawCategoryId: string) => {
        const categoryId = normalizeCategoryId(rawCategoryId);
        if (categoryId <= 0) {
            updateSelectedCategories([], autoApplyCategoryFilters);
            return;
        }

        if (allowMultipleCategories) {
            if (!selectedCategorySet.has(categoryId)) {
                updateSelectedCategories([...selectedCategoryIdsNormalized, categoryId], autoApplyCategoryFilters);
            }
            return;
        }

        updateSelectedCategories([categoryId], autoApplyCategoryFilters);
    };

    const renderCategoryNode = (node: CategoryTreeNode, level: number = 0): React.ReactNode => {
        const categoryId = normalizeCategoryId(node.id);
        if (categoryId <= 0) {
            return null;
        }

        const children = node.children || [];
        const hasChildren = children.length > 0;
        const isSearchMode = normalizedCategorySearch !== '';
        const isExpanded = isSearchMode || expandedCategoryNodes.has(categoryId);
        const isSelected = selectedCategorySet.has(categoryId);
        const categoryCount = Number(node.count);
        const hasCategoryCount = Number.isFinite(categoryCount) && categoryCount >= 0;

        return (
            <div key={categoryId} className="category-tree-node">
                <div
                    className={`category-tree-row ${isSelected ? 'is-selected' : ''}`}
                    style={{paddingLeft: `${level * 18 + 8}px`}}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            className="category-tree-toggle"
                            onClick={() => toggleCategoryExpanded(categoryId)}
                            aria-label={isExpanded ? 'Collapse category' : 'Expand category'}
                        >
                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                        </button>
                    ) : (
                        <span className="category-tree-spacer"></span>
                    )}
                    <label className="category-tree-label">
                        <input
                            type={allowMultipleCategories ? 'checkbox' : 'radio'}
                            name="weka-product-category-selection"
                            checked={isSelected}
                            onChange={() => toggleCategorySelection(categoryId)}
                        />
                        <span>{node.name}</span>
                        {hasCategoryCount && (
                            <span className="category-tree-count">({categoryCount})</span>
                        )}
                    </label>
                </div>
                {hasChildren && isExpanded && (
                    <div className="category-tree-children">
                        {children.map((child) => renderCategoryNode(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    const onProductResultSort = (sort: string) => {
        setSortBy(sort);
        setPage(1);
    };

    const selectedAttributeCount = useMemo(() => {
        return Object.values(selectedAttributesNormalized).reduce((total, termIds) => total + termIds.length, 0);
    }, [selectedAttributesNormalized]);

    const selectedCategoryCount = selectedCategoryIdsNormalized.length;
    const showCategorySidebarPanel = showCategorySidebar && categoryOptions.length > 0;
    const showCategoryFiltersInDrawer = !showCategorySidebarPanel && categoryOptions.length > 0;
    const showAttributeFiltersPanel = showAttributeFilters && attributeFilters.length > 0;
    const hasDrawerFilters = showCategoryFiltersInDrawer || showAttributeFiltersPanel;
    const drawerFilterCount = (showCategoryFiltersInDrawer ? selectedCategoryCount : 0)
        + (showAttributeFiltersPanel ? selectedAttributeCount : 0);
    const drawerTitle = showCategoryFiltersInDrawer && showAttributeFiltersPanel
        ? 'Refine Results'
        : (showCategoryFiltersInDrawer ? 'Category Filters' : 'Attribute Filters');
    const drawerClearLabel = showCategoryFiltersInDrawer && showAttributeFiltersPanel
        ? 'Clear Filters'
        : (showCategoryFiltersInDrawer ? 'Clear Categories' : 'Clear Attributes');
    const drawerApplyLabel = showCategoryFiltersInDrawer && showAttributeFiltersPanel
        ? 'Apply Filters'
        : (showCategoryFiltersInDrawer ? 'Apply Categories' : 'Apply Attributes');
    const drawerHasSelections = (showCategoryFiltersInDrawer && selectedCategoryCount > 0)
        || (showAttributeFiltersPanel && selectedAttributeCount > 0);
    const showDrawerActionBar = (!autoApplyCategoryFilters && showCategoryFiltersInDrawer)
        || showAttributeFiltersPanel;
    const allowDesktopCategorySidebarCollapse = showCategorySidebarPanel
        && effectiveUiSettings.desktop_collapsible_category_sidebar === true;

    const hasActiveSearch = searchTerm.trim() !== ''
        || selectedCategoryCount > 0
        || selectedAttributeCount > 0
        || sortBy !== ''
        || page !== 1;

    const onClearSearch = () => {
        const resetState: SearchCriteria = {
            selectedCategoryIds: [],
            selectedAttributes: {},
            searchTerm: '',
            sortBy: '',
            page: 1,
        };

        setSelectedCategoryIds(resetState.selectedCategoryIds);
        setSelectedAttributes(resetState.selectedAttributes);
        setCategorySearchTerm('');
        setAttributeSearchMap({});
        setSearchTerm(resetState.searchTerm);
        setSortBy(resetState.sortBy);
        setPage(resetState.page);

        doSearch(resetState);
        setFiltersVisible(false);
    };

    const onClearDrawerFilters = () => {
        const resetState: Partial<SearchCriteria> = {
            page: 1,
        };

        if (showCategoryFiltersInDrawer) {
            setSelectedCategoryIds([]);
            setCategorySearchTerm('');
            resetState.selectedCategoryIds = [];
        }

        if (showAttributeFiltersPanel) {
            setSelectedAttributes({});
            setAttributeSearchMap({});
            resetState.selectedAttributes = {};
        }

        setPage(1);
        doSearch(resetState);
    };

    const onApplyDrawerFilters = () => {
        runSearchWithPageReset();
    };

    const onSubmitKeywordSearch = () => {
        const clearedCategoryIds: number[] = [];
        setSelectedCategoryIds(clearedCategoryIds);
        setCategorySearchTerm('');
        runSearchWithPageReset({selectedCategoryIds: clearedCategoryIds});
    };

    const onToggleAttributeTerm = (taxonomy: string, termId: number) => {
        const safeTaxonomy = String(taxonomy || '').trim();
        const safeTermId = Number(termId || 0);
        if (!safeTaxonomy || safeTermId <= 0) {
            return;
        }

        setSelectedAttributes((prev) => {
            const currentIds = prev[safeTaxonomy] || [];
            const nextIds = currentIds.includes(safeTermId)
                ? currentIds.filter((id) => id !== safeTermId)
                : [...currentIds, safeTermId];

            const next = {...prev};
            if (nextIds.length > 0) {
                next[safeTaxonomy] = nextIds;
            } else {
                delete next[safeTaxonomy];
            }

            return next;
        });
    };

    const onSetAttributeTerm = (taxonomy: string, termId: number) => {
        const safeTaxonomy = String(taxonomy || '').trim();
        const safeTermId = Number(termId || 0);
        setSelectedAttributes((prev) => {
            const next = {...prev};
            if (!safeTaxonomy || safeTermId <= 0) {
                delete next[safeTaxonomy];
                return next;
            }

            next[safeTaxonomy] = [safeTermId];
            return next;
        });
    };

    const onClearAttribute = (taxonomy: string) => {
        const safeTaxonomy = String(taxonomy || '').trim();
        if (!safeTaxonomy) {
            return;
        }

        setSelectedAttributes((prev) => {
            const next = {...prev};
            delete next[safeTaxonomy];
            return next;
        });

        setAttributeSearchMap((prev) => {
            const next = {...prev};
            delete next[safeTaxonomy];
            return next;
        });
    };

    useEffect(() => {
        if (skipNextAutomaticSearch.current) {
            skipNextAutomaticSearch.current = false;
            if (shouldRevalidateHydratedState.current) {
                shouldRevalidateHydratedState.current = false;
                doSearch({}, {bypassCache: true, showLoading: false});
            }
            return;
        }

        doSearch();
    }, [sortBy, page, searchEndpoint]);

    const renderCategoryPanel = (showApplyActions: boolean): React.ReactNode => {
        if (allowDesktopCategorySidebarCollapse && isDesktopCategorySidebarCollapsed) {
            return (
                <div className="category-filter-panel category-filter-panel--collapsed">
                    <button
                        type="button"
                        className="category-filter-panel__collapse-toggle"
                        onClick={() => setIsDesktopCategorySidebarCollapsed(false)}
                        aria-expanded={false}
                        aria-label="Expand category filters"
                    >
                        <span className="category-filter-panel__collapse-icon">
                            <i className="fas fa-filter" aria-hidden="true"></i>
                        </span>
                        <span className="category-filter-panel__collapse-label">Filters</span>
                        {selectedCategoryCount > 0 && (
                            <span className="category-filter-panel__collapse-badge">{selectedCategoryCount}</span>
                        )}
                    </button>
                </div>
            );
        }

        return (
            <div className="category-filter-panel">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-semibold mb-0">{allowMultipleCategories ? 'Categories' : 'Category'}</label>
                    <div className="d-flex align-items-center gap-2">
                        {selectedCategoryCount > 0 && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none"
                                onClick={() => clearSelectedCategories()}
                            >
                                Clear
                            </button>
                        )}
                        {allowDesktopCategorySidebarCollapse && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none category-filter-panel__desktop-toggle"
                                onClick={() => setIsDesktopCategorySidebarCollapsed(true)}
                                aria-expanded={true}
                                aria-label="Collapse category filters"
                            >
                                <i className="fas fa-chevron-left" aria-hidden="true"></i>
                            </button>
                        )}
                    </div>
                </div>
                {categoryFilterDisplay === 'dropdown' ? (
                    <SearchableSelect
                        id="category-filter-select"
                        options={flatCategoryOptions.map((category) => {
                            const countLabel = category.count !== null ? ` (${category.count})` : '';
                            const prefix = category.depth > 0 ? `${'- '.repeat(category.depth)}` : '';
                            return {
                                value: category.id,
                                label: `${prefix}${category.name}${countLabel}`,
                                disabled: allowMultipleCategories && selectedCategorySet.has(category.id),
                            };
                        })}
                        value={allowMultipleCategories ? '' : String(selectedCategoryIdsNormalized[0] || '')}
                        placeholder={allowMultipleCategories ? 'Add a category...' : 'All categories'}
                        searchPlaceholder="Search categories..."
                        onChange={(value) => onDropdownCategoryChange(String(value || ''))}
                        disabled={isLoading || isFiltersLoading}
                    />
                ) : (
                    <>
                        <input
                            type="text"
                            className="form-control form-control-sm mb-2"
                            placeholder="Search categories..."
                            value={categorySearchTerm}
                            onChange={(event) => setCategorySearchTerm(event.target.value)}
                        />
                        <div className="category-tree-scroll">
                            {visibleCategoryOptions.length > 0 ? (
                                visibleCategoryOptions.map((node) => renderCategoryNode(node, 0))
                            ) : (
                                <small className="text-muted">No categories found.</small>
                            )}
                        </div>
                    </>
                )}
                {categoryFilterDisplay === 'dropdown' && flatCategoryOptions.length === 0 && (
                    <small className="text-muted">No categories found.</small>
                )}
                {allowMultipleCategories && selectedCategoryCount > 0 && (
                    <div className="category-selected-summary mt-2">
                        <small className="text-muted d-block">
                            {allowMultipleCategories ? `Selected (${selectedCategoryCount}):` : 'Selected:'}
                        </small>
                        <div className="category-selected-tags">
                            {selectedCategoryIdsNormalized.map((categoryId) => (
                                <button
                                    key={`selected-cat-${categoryId}`}
                                    type="button"
                                    className="category-selected-tag"
                                    onClick={() => updateSelectedCategories(
                                        selectedCategoryIdsNormalized.filter((id) => id !== categoryId),
                                        autoApplyCategoryFilters
                                    )}
                                >
                                    {categoryLookup.get(categoryId)?.name || `Category ${categoryId}`}
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {!allowMultipleCategories && selectedCategoryCount > 0 && categoryFilterDisplay === 'dropdown' && (
                    <div className="category-selected-summary mt-2">
                        <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => clearSelectedCategories()}
                        >
                            Clear selected category
                        </button>
                    </div>
                )}
                {categoryFilterDisplay !== 'dropdown' && allowMultipleCategories && selectedCategoryCount > 0 && (
                    <div className="category-selected-summary mt-2">
                        <small className="text-muted d-block">
                            {allowMultipleCategories ? `Selected (${selectedCategoryCount}):` : 'Selected:'}
                        </small>
                        <div className="category-selected-tags">
                            {selectedCategoryIdsNormalized.map((categoryId) => (
                                <span key={`selected-cat-${categoryId}`} className="category-selected-tag">
                                    {categoryLookup.get(categoryId)?.name || `Category ${categoryId}`}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                {showApplyActions && (
                    <div className="find-products-filter-actions mt-3 justify-content-end">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => runSearchWithPageReset({}, false)}
                            disabled={isLoading || isFiltersLoading}
                        >
                            {allowMultipleCategories ? 'Apply Categories' : 'Apply Category'}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderAttributePanel = (): React.ReactNode => (
        <div className="attribute-filter-panel">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-semibold mb-0">Attributes</label>
                {selectedAttributeCount > 0 && (
                    <span className="badge bg-light text-dark border">
                        {selectedAttributeCount} selected
                    </span>
                )}
            </div>
            {attributeFilters.length === 0 ? (
                <div className="text-muted small">No attribute filters available.</div>
            ) : (
                <div className="attribute-filter-groups">
                    {attributeFilters.map((attribute) => {
                        const selectedTermIds = selectedAttributes[attribute.taxonomy] || [];
                        const attributeSearch = String(attributeSearchMap[attribute.taxonomy] || '');
                        const normalizedSearch = attributeSearch.trim().toLowerCase();
                        const displayType = ['checkbox', 'dropdown', 'pill'].includes(String(attribute.display_type || ''))
                            ? String(attribute.display_type)
                            : 'pill';
                        const visibleTerms = normalizedSearch === ''
                            ? attribute.terms
                            : attribute.terms.filter((term) => (
                                String(term.name || '').toLowerCase().includes(normalizedSearch)
                            ));

                        return (
                            <div key={attribute.taxonomy} className="attribute-filter-group">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="attribute-filter-group-label">
                                        {attribute.label}
                                    </span>
                                    {selectedTermIds.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                            onClick={() => onClearAttribute(attribute.taxonomy)}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                {displayType !== 'dropdown' && attribute.terms.length > 8 && (
                                    <input
                                        type="text"
                                        className="form-control form-control-sm mb-2"
                                        placeholder={`Search ${attribute.label.toLowerCase()}...`}
                                        value={attributeSearch}
                                        onChange={(event) => {
                                            const nextValue = event.target.value;
                                            setAttributeSearchMap((prev) => ({
                                                ...prev,
                                                [attribute.taxonomy]: nextValue,
                                            }));
                                        }}
                                    />
                                )}
                                {visibleTerms.length > 0 ? (
                                    <>
                                        {displayType === 'dropdown' && (
                                            <SearchableSelect
                                                id={`attribute-select-${attribute.taxonomy}`}
                                                options={visibleTerms.map((term) => ({
                                                    value: Number(term.id || 0),
                                                    label: String(term.name || ''),
                                                }))}
                                                value={selectedTermIds[0] ?? ''}
                                                placeholder={`Any ${attribute.label.toLowerCase()}`}
                                                searchPlaceholder={`Search ${attribute.label.toLowerCase()}...`}
                                                onChange={(val) => {
                                                    const termId = Number(val || 0);
                                                    onSetAttributeTerm(attribute.taxonomy, termId);
                                                }}
                                            />
                                        )}
                                        {displayType === 'checkbox' && (
                                            <div className="attribute-term-checkbox-list">
                                                {visibleTerms.map((term) => {
                                                    const termId = Number(term.id || 0);
                                                    const isSelected = selectedTermIds.includes(termId);
                                                    return (
                                                        <label key={`${attribute.taxonomy}-${termId}`} className="attribute-term-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => onToggleAttributeTerm(attribute.taxonomy, termId)}
                                                            />
                                                            <span>{term.name}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {displayType === 'pill' && (
                                            <div className="attribute-term-list">
                                                {visibleTerms.map((term) => {
                                                    const termId = Number(term.id || 0);
                                                    const isSelected = selectedTermIds.includes(termId);
                                                    return (
                                                        <button
                                                            key={`${attribute.taxonomy}-${termId}`}
                                                            type="button"
                                                            className={`attribute-term-pill ${isSelected ? 'is-selected' : ''}`}
                                                            onClick={() => onToggleAttributeTerm(attribute.taxonomy, termId)}
                                                        >
                                                            <span>{term.name}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <small className="text-muted">No matching options.</small>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    return (
        <MyAccountPageShell
            id="weka-content"
            className="find-products-container"
            title={headerTitle}
            subtitle={pageSubtitle}
        >
            {(isLoading || isFiltersLoading) && (
                <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
                    <div className="bg-white border rounded shadow-sm p-3" style={{ minWidth: '280px' }}>
                        <LoadingBar height="14px" width="120px" style={{ marginBottom: '12px' }} />
                        <LoadingBar height="18px" width="100%" style={{ marginBottom: '10px' }} />
                        <LoadingBar height="18px" width="82%" style={{ marginBottom: '10px' }} />
                        <LoadingBar height="18px" width="68%" />
                    </div>
                </div>
            )}
            <div
                className={[
                    'find-products-layout',
                    showCategorySidebarPanel ? 'find-products-layout--with-sidebar' : '',
                    allowDesktopCategorySidebarCollapse && isDesktopCategorySidebarCollapsed ? 'find-products-layout--sidebar-collapsed' : '',
                ].filter(Boolean).join(' ')}
            >
                {showCategorySidebarPanel && (
                    <aside className={`find-products-layout__sidebar${allowDesktopCategorySidebarCollapse && isDesktopCategorySidebarCollapsed ? ' is-collapsed' : ''}`}>
                        {renderCategoryPanel(!autoApplyCategoryFilters)}
                    </aside>
                )}
                <div className="find-products-layout__main">
                    {showInlineFitmentSelector && (
                        <FitmentSelector
                            config={fitmentToolConfig}
                            selection={selectedFitmentAttributes}
                            disabled={isLoading || isFiltersLoading}
                            onSelectionChange={applyFitmentSelection}
                        />
                    )}
                    <div className="mb-3">
                        <form onSubmit={(event) => {
                            event.preventDefault();
                            onSubmitKeywordSearch();
                        }}>
                            <div className="mb-3">
                                <div className="input-group weka-search-input-group">
                                    <span className="input-group-text">
                                        <i className="fas fa-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        name="quick-search-products"
                                        id="quick-add"
                                        placeholder={searchPlaceholder}
                                        className="form-control"
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                    />
                                    <button
                                        type="submit"
                                        className="btn weka-search-input-group__button"
                                        disabled={isLoading || isFiltersLoading}
                                    >
                                        <i className="fas fa-search me-2"></i>
                                        Search
                                    </button>
                                    {hasActiveSearch && (
                                        <button
                                            type="button"
                                            className="btn weka-search-input-group__button"
                                            onClick={onClearSearch}
                                            disabled={isLoading || isFiltersLoading}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            {hasDrawerFilters && (
                                <div className="find-products-refine-toggle-wrap">
                                    <button
                                        type="button"
                                        className="find-products-refine-toggle"
                                        onClick={() => setFiltersVisible(!isFiltersVisible)}
                                        disabled={isFiltersLoading}
                                    >
                                        <i className={`fas fa-chevron-${isFiltersVisible ? 'up' : 'down'} me-1`}></i>
                                        {isFiltersVisible ? 'Hide' : 'Show'} {drawerTitle}
                                        {drawerFilterCount > 0 && (
                                            <span className="badge bg-primary ms-2">
                                                {drawerFilterCount}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            )}

                            {hasDrawerFilters && isFiltersVisible && (
                                <div className="find-products-refine-panel">
                                    <div className="row g-3">
                                        {showCategoryFiltersInDrawer && (
                                            <div className={`col-12${showAttributeFiltersPanel ? ' col-lg-5' : ''}`}>
                                                {renderCategoryPanel(false)}
                                            </div>
                                        )}
                                        {showAttributeFiltersPanel && (
                                            <div className={`col-12${showCategoryFiltersInDrawer ? ' col-lg-7' : ''}`}>
                                                {renderAttributePanel()}
                                            </div>
                                        )}
                                        {showDrawerActionBar && (
                                            <div className="col-12 d-flex justify-content-end gap-2 pt-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={onClearDrawerFilters}
                                                    disabled={isLoading || isFiltersLoading || !drawerHasSelections}
                                                >
                                                    {drawerClearLabel}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    onClick={onApplyDrawerFilters}
                                                    disabled={isLoading || isFiltersLoading}
                                                >
                                                    {drawerApplyLabel}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                    <div className={!hasSearched ? 'hidden' : ''}>
                        <ProductResultTable
                            searchResponse={searchResponse}
                            onSort={onProductResultSort}
                            sort={sortBy}
                            templateCsvUrl={templateCsvUrl || ''}
                            uiSettings={effectiveUiSettings}
                            compactDesktop={showCategorySidebarPanel && (!allowDesktopCategorySidebarCollapse || !isDesktopCategorySidebarCollapsed)}
                            productDetailBasePath={searchResponse?.settings?.product_detail_base_path || '/find-products/product'}
                            onPageChange={(newPage) => {
                                if (newPage === page) {
                                    return;
                                }
                                scrollToTopOfResults('smooth');
                                setPage(newPage);
                            }}
                        />
                    </div>
                </div>
            </div>
        </MyAccountPageShell>
    );
};

export default ProductSearchForm
